// Promo de una línea de venta cerrada: original, descuento y final, o el regalo. Puro, sin React.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { shownDiscountCode } from '@/shared/patterns/SalePromotion/discountCode';
import type { SaleAppliedPromotion } from '../api/wizardPromotionsContract';

export type LinePromotionView =
    | { kind: 'GIFT'; valueCents: number }
    | {
          kind: 'DISCOUNT';
          /** Nombre de la promo; ausente si no se conoce (la etiqueta cae al código o «Promoción»). */
          name?: string;
          /** Nombres de todas las promos de la línea, una etiqueta por promo. */
          names?: string[];
          code?: string;
          originalCents: number;
          discountCents: number;
          finalCents: number;
      };

/** Nombre de la promo de la línea entre las que tomó la venta, cruzando por promo y versión. */
export function appliedPromotionName(
    applied: readonly SaleAppliedPromotion[],
    line: Pick<SaleLine, 'promotionId' | 'promotionVersion'>,
): string | undefined {
    if (!line.promotionId || line.promotionVersion === undefined) return undefined;
    return applied.find(
        (p) => p.promotionId === line.promotionId && p.version === line.promotionVersion,
    )?.name;
}

/** Nombres de cada promo de la línea (de `promotionDiscounts` o, en ventas viejas, de `promotionId`). */
export function appliedPromotionNames(
    applied: readonly SaleAppliedPromotion[],
    line: Pick<SaleLine, 'promotionId' | 'promotionVersion' | 'promotionDiscounts'>,
): string[] {
    const refs =
        line.promotionDiscounts && line.promotionDiscounts.length > 0
            ? line.promotionDiscounts.filter((d) => d.discountCents > 0)
            : line.promotionId && line.promotionVersion !== undefined
              ? [{ promotionId: line.promotionId, promotionVersion: line.promotionVersion }]
              : [];
    return refs.flatMap((ref) => {
        const name = applied.find(
            (p) => p.promotionId === ref.promotionId && p.version === ref.promotionVersion,
        )?.name;
        return name ? [name] : [];
    });
}

/** Lo que la fila muestra de la promo; `null` si la línea no trae promo ni código. */
export function linePromotionView(
    line: SaleLine,
    promotionName?: string,
    promotionNames: string[] = [],
): LinePromotionView | null {
    if (line.role === 'GIFT') return { kind: 'GIFT', valueCents: line.originalPriceCents ?? 0 };
    const code = shownDiscountCode(line);
    if (!line.promotionId && !code) return null;
    const finalCents = line.basePriceCents * line.qty - (line.discountCents ?? 0);
    const originalCents = line.originalPriceCents ?? line.basePriceCents * line.qty;
    return {
        kind: 'DISCOUNT',
        ...(line.promotionId && promotionName && { name: promotionName }),
        ...(promotionNames.length > 1 && { names: promotionNames }),
        ...(code && { code }),
        originalCents,
        discountCents: originalCents - finalCents,
        finalCents,
    };
}
